import {
    BrowserMultiFormatReader,
    BarcodeFormat,
    DecodeHintType,
    NotFoundException,
    ChecksumException,
    FormatException,
} from '@zxing/library';
import { prepareZXingModule, readBarcodes } from 'zxing-wasm/reader';

export const ENGINE_WASM = 'wasm';
export const ENGINE_JS = 'js';

// Доля короткой стороны кадра, уходящая в декодер. Ровно этот квадрат
// показывается продавцу как прицельная рамка — оба движка используют
// одну и ту же область, иначе сравнение на замере будет нечестным.
export const ROI_RATIO = 0.7;

// Предохранитель для широких кадров и планшетов: на обычном телефоне область разбора
// и так выходит меньше, но упереться в дорогой getImageData на большой картинке не хочется.
const MAX_DECODE_SIDE = 720;

// Столько подряд идущих ошибок разбора считаем отказом воркера
const WORKER_ERROR_LIMIT = 3;

// ---------------------------------------------------------------------------
// Общее: вырезка центрального квадрата кадра
// ---------------------------------------------------------------------------

function roiOf(video) {
    const vw = video?.videoWidth || 0;
    const vh = video?.videoHeight || 0;
    if (!vw || !vh) return null;

    // Видео растянуто на весь экран через object-fit: cover, то есть в кадр попадает
    // не весь фрейм камеры, а его центральная вырезка. Декодеру отдаём область,
    // рассчитанную от видимой части — иначе прицельная рамка на экране показывала бы
    // одно, а движок читал бы другое, в том числе то, чего продавец вообще не видит.
    const cw = video.clientWidth || vw;
    const ch = video.clientHeight || vh;
    const scale = Math.max(cw / vw, ch / vh);
    const visibleW = Math.min(vw, cw / scale);
    const visibleH = Math.min(vh, ch / scale);

    const side = Math.round(Math.min(visibleW, visibleH) * ROI_RATIO);
    return { sx: Math.round((vw - side) / 2), sy: Math.round((vh - side) / 2), side };
}

/**
 * Сторона прицельной рамки в CSS-пикселях.
 * Совпадает с roiOf() по построению: при object-fit: cover видимая часть кадра
 * ровно вписана в элемент, поэтому сторона ROI на экране = доля меньшей стороны элемента.
 */
export function roiScreenSide(video) {
    const cw = video?.clientWidth || 0;
    const ch = video?.clientHeight || 0;
    if (!cw || !ch) return 0;

    return Math.round(Math.min(cw, ch) * ROI_RATIO);
}

function grabRoiImageData(video, canvas) {
    const roi = roiOf(video);
    if (!roi) return null;

    const side = Math.min(roi.side, MAX_DECODE_SIDE);
    if (canvas.width !== side || canvas.height !== side) {
        canvas.width = side;
        canvas.height = side;
    }

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, roi.sx, roi.sy, roi.side, roi.side, 0, 0, side, side);
    return ctx.getImageData(0, 0, side, side);
}

const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

// ---------------------------------------------------------------------------
// Движок 1: @zxing/library (JS-порт) — то, что стояло изначально
// ---------------------------------------------------------------------------

// Марка ИС МПТ — это DataMatrix. QR оставлен для совместимости.
// Без подсказок MultiFormatReader перебирает ещё и Aztec, PDF417, EAN, Code128,
// ITF и Codabar на каждом кадре — попыток в секунду становится в разы меньше.
const JS_HINTS = new Map();
JS_HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.DATA_MATRIX, BarcodeFormat.QR_CODE]);
JS_HINTS.set(DecodeHintType.TRY_HARDER, true);

/**
 * Декодирует не весь кадр, а центральный квадрат.
 * drawFrameOnCanvas и createCaptureCanvas — штатные точки расширения библиотеки.
 */
class RoiMultiFormatReader extends BrowserMultiFormatReader {
    createCaptureCanvas(mediaElement) {
        const roi = roiOf(mediaElement);
        // Размеры видео ещё неизвестны — отдаём управление базовой реализации,
        // корректный размер выставится в drawFrameOnCanvas на первом же кадре.
        if (!roi) return super.createCaptureCanvas(mediaElement);

        const canvas = document.createElement('canvas');
        canvas.width = roi.side;
        canvas.height = roi.side;
        return canvas;
    }

    drawFrameOnCanvas(video) {
        const roi = roiOf(video);
        if (!roi) return super.drawFrameOnCanvas(video);

        const canvas = this.getCaptureCanvas(video);
        if (canvas.width !== roi.side || canvas.height !== roi.side) {
            canvas.width = roi.side;
            canvas.height = roi.side;
        }

        const ctx = this.getCaptureCanvasContext(video);
        ctx.drawImage(video, roi.sx, roi.sy, roi.side, roi.side, 0, 0, roi.side, roi.side);
    }
}

function createJsEngine() {
    const reader = new RoiMultiFormatReader(JS_HINTS, 100);

    return {
        name: ENGINE_JS,
        label: 'ZXing JS',

        async start({ video, stream, onResult }) {
            await reader.decodeFromStream(stream, video, (result, err) => {
                if (result) {
                    onResult(result.getText());
                    return;
                }

                // NotFound / Checksum / Format прилетают на каждом неудачном кадре —
                // это штатная работа декодера, а не ошибка для продавца.
                const benign = err instanceof NotFoundException
                    || err instanceof ChecksumException
                    || err instanceof FormatException;

                if (err && !benign) {
                    console.error('Decode error:', err);
                }
            });
        },

        stop() {
            // Только разбор кадров. reset() у @zxing/library дополнительно гасит треки
            // переданного потока, а поток принадлежит компоненту: он держит камеру
            // включённой между попытками, чтобы после ошибки не переспрашивать доступ.
            reader.stopContinuousDecode();
        },
    };
}

// ---------------------------------------------------------------------------
// Движок 2: ZXing-C++ через WebAssembly
// ---------------------------------------------------------------------------

const WASM_OVERRIDES = {
    // По умолчанию пакет тянет .wasm с jsDelivr. Раздаём со своего домена:
    // в Telegram WebView внешний CDN — лишняя точка отказа.
    locateFile: (path, prefix) => (
        path.endsWith('.wasm')
            ? `${process.env.PUBLIC_URL || ''}/zxing_reader.wasm`
            : `${prefix}${path}`
    ),
};

const WASM_READER_OPTIONS = {
    formats: ['DataMatrix', 'QRCode'],
    // Критично: по умолчанию стоит HRI, и GS1-марка вернулась бы как
    // "(01)046...(21)..." со скобками. Plain отдаёт то же, что @zxing/library,
    // иначе бэкенд отбракует все коды.
    textMode: 'Plain',
    tryHarder: true,
    tryRotate: true,
    tryInvert: true,
    tryDownscale: true,
    maxNumberOfSymbols: 1,
    binarizer: 'LocalAverage',
};

prepareZXingModule({ overrides: WASM_OVERRIDES });

// Воркер один на страницу: пересоздавать его на каждое возобновление сканирования
// значило бы каждый раз заново поднимать wasm-модуль
let decoderWorker = null;
let decoderWorkerBroken = false;

function getDecoderWorker() {
    if (decoderWorker || decoderWorkerBroken) return decoderWorker;

    let worker;
    try {
        worker = new Worker(new URL('./decoder.worker.js', import.meta.url));
    } catch (e) {
        console.warn('Воркер недоступен, разбор пойдёт в основном потоке:', e);
        decoderWorkerBroken = true;
        return null;
    }

    const pending = new Map();
    let lastId = 0;
    let errorsInRow = 0;

    // Дальше работаем в основном потоке: лучше подлагивающая картинка, чем мёртвый сканер
    const giveUpOnWorker = (reason) => {
        console.error('Разбор в воркере отключён:', reason);
        decoderWorkerBroken = true;
        decoderWorker = null;
        pending.forEach(resolve => resolve(null));
        pending.clear();
        worker.terminate();
    };

    worker.onmessage = (event) => {
        const { id, text, error } = event.data || {};
        const resolve = pending.get(id);
        if (!resolve) return;

        pending.delete(id);

        if (error) {
            // Воркер ловит ошибки сам и остаётся живым, поэтому onerror при неподнявшемся
            // wasm-модуле не сработает — считаем отказы руками
            console.error('WASM decode error:', error);
            if (++errorsInRow >= WORKER_ERROR_LIMIT) giveUpOnWorker(error);
        } else {
            errorsInRow = 0;
        }

        resolve(text || null);
    };

    worker.onerror = (e) => giveUpOnWorker(e?.message || e);

    decoderWorker = {
        decode(imageData) {
            const id = ++lastId;
            return new Promise((resolve) => {
                pending.set(id, resolve);
                // Буфер уходит без копирования
                worker.postMessage(
                    { id, buffer: imageData.data.buffer, width: imageData.width, height: imageData.height },
                    [imageData.data.buffer]
                );
            });
        },
    };

    return decoderWorker;
}

/** Прогревает движок заранее, чтобы первый скан не ждал загрузки wasm. */
export function preloadWasmEngine() {
    const worker = getDecoderWorker();
    if (worker) {
        // Пустой кадр только ради инициализации модуля в воркере
        return worker.decode(new ImageData(1, 1)).catch(() => null);
    }

    return prepareZXingModule({ overrides: WASM_OVERRIDES, fireImmediately: true })
        .catch(e => console.error('WASM preload failed:', e));
}

function createWasmEngine() {
    const canvas = document.createElement('canvas');
    let stopped = false;

    const decodeInMainThread = async (imageData) => {
        const results = await readBarcodes(imageData, WASM_READER_OPTIONS);
        const hit = results.find(r => r.isValid && r.text);
        return hit ? hit.text : null;
    };

    return {
        name: ENGINE_WASM,
        label: 'ZXing-C++ WASM',

        async start({ video, stream, onResult }) {
            stopped = false;
            video.srcObject = stream;

            try {
                await video.play();
            } catch (e) {
                // AbortError прилетает, если поток остановили раньше, чем видео стартовало
                if (stopped) return;
                throw e;
            }

            const loop = async () => {
                while (!stopped) {
                    const imageData = grabRoiImageData(video, canvas);

                    if (imageData) {
                        try {
                            const worker = getDecoderWorker();
                            const text = worker
                                ? await worker.decode(imageData)
                                : await decodeInMainThread(imageData);

                            if (stopped) return;

                            if (text) {
                                onResult(text);
                                // Результат мог быть отклонён обработчиком — например, этот код
                                // уже отправляли. Тогда разбор никто не останавливал и цикл
                                // обязан продолжиться, иначе сканер замолкает навсегда.
                                if (stopped) return;
                            }
                        } catch (e) {
                            console.error('WASM decode error:', e);
                        }
                    }

                    await nextFrame();
                }
            };

            loop();
        },

        stop() {
            stopped = true;
        },
    };
}

export function createEngine(name) {
    return name === ENGINE_JS ? createJsEngine() : createWasmEngine();
}
